import React, { useMemo, useState } from 'react';
import { CheckCircle2, HelpCircle, Sparkles } from 'lucide-react';
import { BackToListRow, Button } from '../ui';
import RankingExplainerModal from '../discovery/RankingExplainerModal';
import { SubStepPills } from '../wizard';
import {
  componentKeyOf,
  componentPhaseOf,
  normalizeSupplier,
  suppliersOf,
  keySpecsOf,
} from '../discovery/discoveryModel';
import { listingsOf, pickKeysOf, tickedSupplierCount } from '../../hooks/shortlistListings';
import { discoveryLayoutOf } from '../discovery/listingGroups';
import DiscoveryResearchStatus from '../discovery/DiscoveryResearchStatus';
import DiscoveryStepBar from '../discovery/DiscoveryStepBar';
import ShortlistSavePopup from '../discovery/ShortlistSavePopup';
import { useShortlistSelection } from '../../hooks/useShortlistSelection';
import { ResearchSummary } from '../../types/bom.types';
import { RFQ } from '../../types';
import { parseLeadWindowWeeks } from '../discovery/discoveryInsights';
import ComponentListPane from '../discovery/ComponentListPane';
import ComponentDetailPane from '../discovery/ComponentDetailPane';
import { useIsMobile } from '../../hooks/useIsMobile';
import { saveOutreachNote } from '../../utils/outreachNotes';
import { useReadOnly } from '../../contexts/WizardAccessContext';
import { useReportRobbieUiContext } from '../../contexts/RobbieUiContext';

/**
 * Wizard step 3c — Supplier Discovery, v2.1 negotiation-intelligence layout
 * (`design/Supplier Discovery v2.dc.html`): component list on the left;
 * per-component picks, other-suppliers accordion and trust matrix on the
 * right. Shortlist persistence keeps the existing batch endpoint; deltas and
 * schedule context are computed from the buyer's own requirements and all
 * displayed numbers come from stored research (honest-UI rule).
 */
interface SupplierDiscoveryProps {
  bomId?: string;
  components: any[];
  onExportToExcel: () => Promise<void>;
  onRetryComponent: (componentId: string) => Promise<void>;
  isExporting: boolean;
  retryingComponents: Record<string, boolean>;
  onPrevious: () => void;
  onNext?: () => void;
  /** Buyer requirements (criteria step) — powers lead-window + compliance context and the ranking explainer. */
  requirements?: {
    desiredLeadTime?: string;
    complianceRequirements?: string[];
    tradeoffPriorities?: unknown[];
    deliveryLocation?: { formattedAddress?: string };
    sourcingLocation?: string;
  };
  /** Prefills Robbie's docked input (follow-up from the ranking explainer). */
  onAskRobbie?: (text: string) => void;
  onSubStepSelect?: (id: string) => void;
  /** Whole-BOM research progress from the status poll. */
  research?: ResearchSummary | null;
  rfq?: Pick<RFQ, 'rfqId' | 'emailPromise'>;
  /** "Use this instead": switch a line to an alternative spec. */
  onSwapSpec?: (componentKey: string, specGroup: string) => void;
  /** `${componentKey}|${specGroup}` of the switch in flight. */
  swappingSpec?: string | null;
}

const PILLS = [
  { id: 'context', label: 'Context', state: 'done' as const },
  { id: 'validation', label: 'Validation', state: 'done' as const },
  { id: 'discovery', label: 'Discovery', state: 'active' as const },
];

const SupplierDiscovery: React.FC<SupplierDiscoveryProps> = ({
  bomId,
  components,
  onRetryComponent,
  retryingComponents,
  onPrevious,
  onNext,
  requirements,
  onAskRobbie,
  onSubStepSelect,
  research = null,
  rfq,
  onSwapSpec,
  swappingSpec = null,
}) => {
  const readOnly = useReadOnly();
  const [selectedKey, setSelectedKey] = useState<string | null>(
    components.length > 0 ? componentKeyOf(components[0], 0) : null,
  );
  const [filter, setFilter] = useState('');
  // On a phone the two panes are two screens: the components are where you
  // land, and picking one opens its suppliers full width. The selection
  // above still auto-lands on the first component — that decides WHICH
  // detail opens, not whether it is showing.
  const isMobile = useIsMobile();
  const [detailOpen, setDetailOpen] = useState(false);
  const [explainerOpen, setExplainerOpen] = useState(false);

  const leadWindowWeeks = parseLeadWindowWeeks(requirements?.desiredLeadTime);
  const requiredCompliance = Array.isArray(requirements?.complianceRequirements)
    ? requirements?.complianceRequirements
    : undefined;

  const shortlist = useShortlistSelection(bomId, components);
  const { selected } = shortlist;

  const listItems = useMemo(
    () =>
      components.map((c: any, i: number) => {
        const key = componentKeyOf(c, i);
        const layout = discoveryLayoutOf(listingsOf(c));
        return {
          key,
          name: c?.name || 'Unnamed component',
          partNumber: c?.partNumber || undefined,
          mpn: c?.mpn || undefined,
          phase: componentPhaseOf(c),
          supplierCount: suppliersOf(c).length,
          pickedCount: tickedSupplierCount(c, selected[key] || []),
          matchingSupplierCount: layout.main.length,
          otherSpecCount: layout.alternatives.length,
          researchState: c?.researchState,
          attempt: c?.attempt,
          maxAttempts: c?.maxAttempts,
        };
      }),
    [components, selected],
  );

  const query = filter.trim().toLowerCase();
  const visibleItems = query
    ? listItems.filter((it) => it.name.toLowerCase().includes(query) || it.partNumber?.toLowerCase().includes(query) || it.mpn?.toLowerCase().includes(query))
    : listItems;

  const selectedIndex = components.findIndex((c: any, i: number) => componentKeyOf(c, i) === selectedKey);
  const selectedComponent = selectedIndex >= 0 ? components[selectedIndex] : null;
  const selectedComponentKey = selectedComponent ? componentKeyOf(selectedComponent, selectedIndex) : null;
  const selectedSupplierViews = useMemo(
    () => (selectedComponent ? suppliersOf(selectedComponent).map(normalizeSupplier) : []),
    [selectedComponent],
  );

  const totalShortlisted = listItems.reduce((acc, it) => acc + it.pickedCount, 0);
  const withSuppliers = listItems.filter((it) => it.supplierCount > 0).length;
  const continueToOutreach = onNext
    ? async () => {
        if (await shortlist.saveForContinue()) onNext();
      }
    : undefined;
  const anyPicksExist = components.some((c: any) => pickKeysOf(c).length > 0);

  // Robbie sees what the buyer sees: the selected component (by id and name)
  // and how many suppliers are ticked for it. Pointers only — the backend
  // expands them from the stored BOM.
  const tickedForSelected = selectedComponent ? shortlist.pickedOn(selectedComponent, selectedIndex) : 0;
  useReportRobbieUiContext(
    selectedComponent
      ? {
          view: 'supplier-discovery',
          componentId: selectedComponent.componentId || selectedComponentKey || undefined,
          componentName: selectedComponent.name || selectedComponent.partNumber || undefined,
          detail: `${selectedSupplierViews.length} suppliers on screen, ${tickedForSelected} ticked for shortlist`,
        }
      : { view: 'supplier-discovery' },
  );

  return (
    <div className="flex flex-col gap-s5 font-sans">
      <div className="flex flex-wrap items-center gap-s3">
        <SubStepPills items={PILLS} onSelect={onSubStepSelect} />
        <div className="ml-auto flex flex-wrap items-center gap-s3">
          {anyPicksExist && !readOnly && (
            <Button variant="secondary" size="sm" iconLeft={<Sparkles />} onClick={shortlist.pickAll}>
              Shortlist Robbie's picks for all
            </Button>
          )}
          <Button variant="ghost" size="sm" iconLeft={<HelpCircle />} onClick={() => setExplainerOpen(true)}>
            Why these suppliers?
          </Button>
          <span className="inline-flex items-center gap-s2 rounded-pill border border-success-border bg-success-bg px-[11px] py-[5px] text-body-sm font-semibold text-success-ink">
            <CheckCircle2 className="h-3 w-3" />
            {totalShortlisted} shortlisted
          </span>
          <span className="text-body-sm text-ink-muted">across {components.length} components</span>
        </div>
      </div>

      <DiscoveryResearchStatus
        bomId={bomId}
        rfqId={rfq?.rfqId}
        emailPromise={rfq?.emailPromise}
        summary={research}
        items={listItems}
        onRetryComponent={readOnly ? undefined : onRetryComponent}
      />

      <div
        className={
          isMobile
            ? 'flex flex-col overflow-hidden rounded-card border border-line bg-surface'
            : 'grid h-[calc(100vh-330px)] min-h-[440px] grid-cols-[minmax(150px,220px)_minmax(0,1fr)] overflow-hidden rounded-card border border-line bg-surface'
        }
      >
        {isMobile && detailOpen && (
          <BackToListRow label="All components" count={components.length} onBack={() => setDetailOpen(false)} />
        )}
        {(!isMobile || !detailOpen) && (
          <ComponentListPane
            items={visibleItems}
            totalCount={components.length}
            selectedKey={selectedKey}
            filter={filter}
            onFilterChange={setFilter}
            onSelect={(key) => {
              setSelectedKey(key);
              setDetailOpen(true);
            }}
            onRetry={(key) => onRetryComponent(key)}
            retrying={retryingComponents}
          />
        )}
        {isMobile && !detailOpen ? null : selectedComponent && selectedComponentKey ? (
          <ComponentDetailPane
            key={selectedComponentKey}
            name={selectedComponent.name || 'Unnamed component'}
            partNumber={selectedComponent.partNumber || undefined}
            mpn={selectedComponent.mpn || undefined}
            quantity={typeof selectedComponent.quantity === 'number' ? selectedComponent.quantity : undefined}
            description={selectedComponent.description || selectedComponent.specifications || undefined}
            keySpecs={keySpecsOf(selectedComponent)}
            phase={componentPhaseOf(selectedComponent)}
            researchState={selectedComponent.researchState}
            researchStage={selectedComponent.researchStage}
            knownSuppliers={selectedComponent.knownSuppliers}
            attempt={selectedComponent.attempt}
            maxAttempts={selectedComponent.maxAttempts}
            startsInMinutes={selectedComponent.startsInMinutes}
            suppliers={selectedSupplierViews}
            tickedKeys={selected[selectedComponentKey] || []}
            onToggleListing={(listingKey) => shortlist.toggle(selectedComponentKey, listingKey)}
            onUseAlternative={onSwapSpec && ((specGroup) => onSwapSpec(selectedComponentKey, specGroup))}
            switchingSpec={
              swappingSpec?.startsWith(`${selectedComponentKey}|`) ? swappingSpec.slice(selectedComponentKey.length + 1) : null
            }
            onRetry={() => onRetryComponent(selectedComponentKey)}
            isRetrying={!!retryingComponents[selectedComponentKey]}
            targetPrice={typeof selectedComponent.targetPrice === 'number' ? selectedComponent.targetPrice : undefined}
            leadWindowWeeks={leadWindowWeeks}
            requiredCompliance={requiredCompliance}
            onAddToFirstMessage={
              bomId && !readOnly
                ? (supplierName, note) => saveOutreachNote(bomId, selectedComponentKey, supplierName, note)
                : undefined
            }
          />
        ) : (
          <div className="flex flex-col items-center justify-center gap-s3 text-ink-muted">
            <p className="m-0 text-body-md">Select a component to review its suppliers</p>
          </div>
        )}
      </div>

      <RankingExplainerModal
        open={explainerOpen}
        onClose={() => setExplainerOpen(false)}
        requirements={requirements}
        onAskRobbie={onAskRobbie ? () => onAskRobbie('Why these suppliers?') : undefined}
      />

      <DiscoveryStepBar
        readOnly={readOnly}
        totalComponents={components.length}
        withSuppliers={withSuppliers}
        totalPicked={shortlist.totalPicked}
        componentsPicked={shortlist.componentsPicked}
        saving={shortlist.saveState === 'saving'}
        onBack={onPrevious}
        onContinue={readOnly ? onNext : continueToOutreach}
      />
      <ShortlistSavePopup
        state={shortlist.saveState}
        picks={shortlist.totalPicked}
        onRetry={continueToOutreach ?? shortlist.dismissSave}
        onClose={shortlist.dismissSave}
      />
    </div>
  );
};

export default SupplierDiscovery;
